/**
 * <mobile-app-switcher> — c-app-showcase section.
 * Scoped to its own subtree (querySelectorAll runs on `this`, not
 * `document`), so multiple instances on the same page never cross-fire.
 */
class MobileAppSwitcher extends HTMLElement {
  connectedCallback() {
    this.buttons = this.querySelectorAll('.c-app-btn');
    this.images = this.querySelectorAll('.c-app-image');
    this.captions = this.querySelectorAll('.c-app-caption');

    this.buttons.forEach((button) => {
      button.addEventListener('click', () => {
        this.setActive(button.dataset.index);

        // Horizontal: keep the clicked button visible/centered within its
        // own scroll-snap row. (No page-level vertical scrollIntoView here
        // anymore — it was jumping the whole page around on mobile.)
        button.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      });
    });

    this.setupSwipe();
  }

  setupSwipe() {
    const screenWrapper = this.querySelector('.c-app-screen-wrapper');
    if (!screenWrapper) return;

    const SWIPE_THRESHOLD = 50;

    // Shared by touch and mouse: startX - endX positive means the
    // pointer moved right-to-left (swipe/drag left / next), negative
    // means left-to-right (swipe/drag right / prev). Reuses the existing
    // button click handler entirely (active state, scroll-into-view)
    // instead of duplicating that logic here.
    const navigate = (diff) => {
      const activeButton = this.querySelector('.c-app-btn.is-active');
      if (!activeButton) return;

      const currentIndex = parseInt(activeButton.dataset.index, 10);
      const lastIndex = this.buttons.length - 1;
      let newIndex = currentIndex;

      if (diff > SWIPE_THRESHOLD) {
        newIndex = Math.min(currentIndex + 1, lastIndex);
      } else if (diff < -SWIPE_THRESHOLD) {
        newIndex = Math.max(currentIndex - 1, 0);
      }

      if (newIndex === currentIndex) return;

      const targetButton = this.querySelector('.c-app-btn[data-index="' + newIndex + '"]');
      if (targetButton) targetButton.click();
    };

    // Touch (mobile).
    let touchStartX = 0;

    screenWrapper.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    });

    screenWrapper.addEventListener('touchend', (e) => {
      const touchEndX = e.changedTouches[0].screenX;
      navigate(touchStartX - touchEndX);
    });

    // Mouse drag (desktop).
    let isDragging = false;
    let mouseStartX = 0;

    screenWrapper.addEventListener('mousedown', (e) => {
      isDragging = true;
      mouseStartX = e.pageX;
    });

    // Stop the browser's native "ghost image" drag behavior when dragging
    // over an <img>.
    screenWrapper.addEventListener('dragstart', (e) => {
      e.preventDefault();
    });

    const endDrag = (e) => {
      if (!isDragging) return;
      isDragging = false;
      navigate(mouseStartX - e.pageX);
    };

    screenWrapper.addEventListener('mouseup', endDrag);
    screenWrapper.addEventListener('mouseleave', endDrag);
  }

  setActive(index) {
    this.buttons.forEach((button) => {
      button.classList.toggle('is-active', button.dataset.index === index);
    });
    this.images.forEach((image) => {
      image.classList.toggle('is-active', image.dataset.index === index);
    });
    this.captions.forEach((caption) => {
      caption.classList.toggle('is-active', caption.dataset.index === index);
    });
  }
}

customElements.define('mobile-app-switcher', MobileAppSwitcher);
